// Your rooms and Listed rooms (MAJLIS-126; D-55, UI-C35; the owner-approved mock-up
// docs/design/rooms-list-mockup-2026-09-25.html, frames (a), (b), (c); the consultant's twelve rulings of 2026-09-26).
//
// Two sections on the landing, ABOVE the New / Existing form (ruling 2) and under the My majlis row, each row the
// record row's shape (UI-S3) - one button whose accessible name reads the whole row:
//   YOUR ROOMS    this phone's own memory (room/yourRooms.ts): OPEN rooms only (MAJLIS-145, 2026-09-30: an ended
//                 room is reached by the My majlis row), each rejoining with one tap by the room id (P-36 rejoin),
//                 with its start time and, for a passive room, the Passive tag. The empty line is shown (ruling 1).
//   LISTED ROOMS  GET /api/rooms/listed: the name, "Hosted by {name}", "{n} seated" and the kind - "Open", or the
//                 drawn KEY with "Code needed" (role=img) - and "Full" (not a button) at the seat limit.
// MAJLIS-172 (component-rules.md section 1; the owner-approved screen-rules mock-up of 2026-10-05, frame (i)): in Listed
// rooms the WHOLE ROW is the tap and nothing inside it is a filled pill. A room that can be joined shows only the
// chevron; a room with a condition shows ONE muted state chip - "Code needed" or "Full" - and nothing else (no key icon);
// a full room is dimmed (dashed border, muted text) and has no chevron (it is not a button). `listedState` is the rule.
// At most LANDING_SECTION_ROWS rows each, then "Show all (n)" (ASSUMPTION). NEVER A CODE anywhere here: nothing in
// either section is given one (roomsList.test.tsx searches the landing's DOM for it).
import { useState, type ReactNode } from "react";
import { LANDING_SECTION_ROWS } from "../constants";
import { useI18n, type StringKey, type Translate } from "../i18n";
import { startedWhen } from "../i18n/when";
import type { ListedRoom } from "../protocol/rooms";
import { startOf, type YourRoom } from "../room/yourRooms";
import { ChevronEndIcon, CloseIcon, KeyIcon } from "./icons";

/** A template with one {name}, the name isolated in a <bdi> (a name keeps its own direction inside the sentence). */
export function withName(t: Translate, key: StringKey, name: string): ReactNode {
  const marker = "\u0000";
  const [before, after = ""] = t(key, { name: marker }).split(marker);
  return (
    <>
      {before}
      <bdi>{name}</bdi>
      {after}
    </>
  );
}

/** The name a room of yours is shown by: its own, or "{host}'s majlis" when it was never listed. */
export function yourRoomName(t: Translate, r: Pick<YourRoom, "name" | "host">): string {
  return r.name ?? t("defaultRoomName", { name: r.host ?? "" });
}

function Head({ title, hint, spec }: { title: string; hint: string; spec: string }) {
  return (
    <div className="rooms-head" data-spec={spec}>
      <h2 className="records-section">{title}</h2>
      <span className="hint">{hint}</span>
    </div>
  );
}

function Capped<T>({ list, render }: { list: readonly T[]; render: (item: T) => ReactNode }) {
  const { t } = useI18n();
  const [all, setAll] = useState(false);
  const shown = all ? list : list.slice(0, LANDING_SECTION_ROWS);
  return (
    <>
      <ul className="records-list">{shown.map(render)}</ul>
      {!all && list.length > LANDING_SECTION_ROWS && (
        <button type="button" className="text-link show-all" onClick={() => setAll(true)}>
          {t("showAll", { n: list.length })}
        </button>
      )}
    </>
  );
}

/** MAJLIS-145 (UI-C35, UI-S3, P-38): a passive room's tag, in the entry screen's own word for the mode. */
export function PassiveTag() {
  const { t } = useI18n();
  return (
    <span className="kind-badge kind-passive" data-spec="UI-C35 P-38 D-57">
      {t("modePassive")}
    </span>
  );
}

/** MAJLIS-172: what a listed room's row shows at its end - nothing but the chevron (it can be joined), or ONE muted state
 * chip: "Code needed" (joinable, with the chevron) or "Full" (dimmed, no chevron). */
export type ListedState = { chip: StringKey | null; chevron: boolean; dimmed: boolean };
export function listedState(room: Pick<ListedRoom, "seated" | "max_seats" | "kind">): ListedState {
  if (room.seated >= room.max_seats) return { chip: "kindFull", chevron: false, dimmed: true };
  if (room.kind === "open") return { chip: null, chevron: true, dimmed: false };
  return { chip: "kindKey", chevron: true, dimmed: false };
}

function StateChip({ room }: { room: ListedRoom }) {
  const { t } = useI18n();
  const { chip } = listedState(room);
  return chip === null ? null : (
    <span className="state-chip" data-spec="UI-C35 D-55">
      {t(chip)}
    </span>
  );
}

/** The chosen listed room's card at the top of the form (mock-up (c) of MAJLIS-126; not a list row, unchanged). */
export function KindBadge({ room }: { room: ListedRoom }) {
  const { t } = useI18n();
  if (room.seated >= room.max_seats) return <span className="kind-badge kind-full">{t("kindFull")}</span>;
  if (room.kind === "open") return <span className="kind-badge kind-open">{t("kindOpen")}</span>;
  return (
    <span className="kind-badge kind-key" role="img" aria-label={t("kindKey")} data-spec="UI-C35 D-55">
      <KeyIcon />
      <span aria-hidden="true">{t("kindKey")}</span>
    </span>
  );
}

export function RoomSections({
  yours,
  listed,
  onRejoin,
  onChoose,
  now,
}: {
  yours: readonly YourRoom[];
  listed: readonly ListedRoom[];
  onRejoin: (roomId: string) => void;
  onChoose: (room: ListedRoom) => void;
  /** MAJLIS-145: the app's injected wall clock, read when the landing renders ("Today", "Yesterday") */
  now: number;
}) {
  const { lang, t } = useI18n();
  // MAJLIS-145 (UI-C35, consultant 2026-09-30): OPEN rooms only, the most recently visited first - an ended room's door
  // is the My majlis row above (the phone still remembers it, room/yourRooms.ts, but does not list it here)
  const open = yours.filter((r) => r.ended === null);
  const kindWord = (r: ListedRoom) =>
    r.seated >= r.max_seats ? t("kindFull") : r.kind === "open" ? t("kindOpen") : t("kindKey");
  return (
    <>
      <Head title={t("yourRooms")} hint={t("yourRoomsHint")} spec="UI-C35 D-55" />
      {open.length === 0 ? (
        <p className="rooms-empty">{t("noYourRooms")}</p>
      ) : (
        <Capped
          list={open}
          render={(r) => {
            const shown = r.name ?? withName(t, "defaultRoomName", r.host ?? "");
            const label = yourRoomName(t, r);
            const role = r.role === "host" ? t("roleHost") : t("roleGuest");
            const seated = t("seatedN", { n: r.seated });
            const when = startedWhen(lang, t, startOf(r), now); // MAJLIS-145: the room's start (P-35), else the first visit
            return (
              <li key={r.room_id}>
                <button
                  type="button"
                  className="room-row"
                  aria-label={[label, role, seated, when, ...(r.passive ? [t("modePassive")] : [])].join(", ")}
                  onClick={() => onRejoin(r.room_id)}
                  data-spec="UI-C35 D-55 P-35 P-36"
                >
                  <span className="record-row-main">
                    <span className="room-row-name" dir="auto">
                      {shown}
                    </span>
                    <span className="record-row-meta">
                      <span className="live-dot" aria-hidden="true" />
                      {role} · {seated}
                    </span>
                    <span className="record-row-meta room-row-when">{when}</span>
                  </span>
                  {r.passive && <PassiveTag />}
                  <ChevronEndIcon />
                </button>
              </li>
            );
          }}
        />
      )}
      <Head title={t("listedRooms")} hint={t("listedRoomsHint")} spec="UI-C35 D-55 P-35" />
      {listed.length === 0 ? (
        <p className="rooms-empty">{t("noListedRooms")}</p>
      ) : (
        <Capped
          list={listed}
          render={(r) => {
            const state = listedState(r);
            const body = <ListedBody room={r} />;
            return (
              <li key={r.handle}>
                {state.dimmed ? (
                  <div className="room-row ended" data-spec="UI-C35 D-55 P-35">
                    {body}
                    <StateChip room={r} />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="room-row"
                    aria-label={`${r.name}, ${t("hostedBy", { name: r.host_name })}, ${t("seatedN", { n: r.seated })}, ${kindWord(r)}`}
                    onClick={() => onChoose(r)}
                    data-spec="UI-C35 D-55 P-35"
                  >
                    {body}
                    <StateChip room={r} />
                    {state.chevron && <ChevronEndIcon />}
                  </button>
                )}
              </li>
            );
          }}
        />
      )}
    </>
  );
}

function ListedBody({ room }: { room: ListedRoom }) {
  const { t } = useI18n();
  return (
    <span className="record-row-main">
      <span className="room-row-name" dir="auto">
        {room.name}
      </span>
      {/* MAJLIS-167 (item 8): one line, cut with an ellipsis like the header's mode line - never wrapped */}
      <span className="record-row-meta listed-meta">
        {withName(t, "hostedBy", room.host_name)} · {t("seatedN", { n: room.seated })}
      </span>
    </span>
  );
}

/** The listed room chosen from the list, as a card at the top of the entry form (mock-up (c)), with an × back. */
export function ChosenRoom({ room, onClear }: { room: ListedRoom; onClear: () => void }) {
  const { t } = useI18n();
  return (
    <div className="chosen-room" data-spec="UI-C35 D-55 P-36">
      <ListedBody room={room} />
      <KindBadge room={room} />
      <button type="button" className="icon-button" aria-label={t("notThisRoom")} onClick={onClear}>
        <CloseIcon />
      </button>
    </div>
  );
}
